import { Badge } from '@/components/ui/badge';
import { Mail, MessageSquare, Phone } from 'lucide-react';

const CHANNEL_LABELS = {
  email: 'Email',
  teams: 'Teams',
  whatsapp: 'WhatsApp',
  webchat: 'WebChat',
};

function channelIcon(channel) {
  if (channel === 'email') return Mail;
  if (channel === 'whatsapp') return Phone;
  return MessageSquare;
}

export function getConversationDisplay(conv) {
  const channel = (conv?.channel || 'webchat').toLowerCase();
  const summary = conv?.channel_context?.summary || {};
  const lastMsg = conv?.messages?.length
    ? conv.messages[conv.messages.length - 1]
    : null;
  const previewSource = lastMsg?.content || conv?.messages?.[0]?.content || '';
  const preview = previewSource
    ? `${previewSource.slice(0, 80)}${previewSource.length > 80 ? '…' : ''}`
    : 'Sin mensajes';

  let primary = conv?.conversation_title || '';
  let secondary = '';

  if (channel === 'email' && (summary.headline || summary.counterparty_email)) {
    primary = summary.headline || primary || 'Sin asunto';
    const name = summary.counterparty_name || summary.counterparty_email || '';
    const email = summary.counterparty_email || '';
    secondary = name && email && name !== email ? `${name} · ${email}` : (name || email);
    if (summary.is_likely_forward) {
      secondary = secondary ? `${secondary} · Reenvío` : 'Reenvío';
    }
  } else {
    const firstUser = (conv?.messages || []).find((m) => m.sender_type === 'user');
    secondary = firstUser?.sender_name || conv?.channel_user_id || '';
    if (!primary) primary = secondary || conv?.conversation_id?.slice(0, 8) || 'Conversación';
  }

  return {
    channel,
    primary,
    secondary,
    preview,
    ticketId: conv?.ticket_id || summary.ticket_id || null,
    pendingOutbound: conv?.pending_outbound?.status === 'pending_approval',
  };
}

export default function ConversationListItem({
  conv,
  selected,
  onClick,
  formatTime,
  trailing,
  hasMergeSuggestion,
  className = '',
}) {
  const d = getConversationDisplay(conv);
  const Icon = channelIcon(d.channel);
  const channelLabel = CHANNEL_LABELS[d.channel] || d.channel;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-brand-border transition-colors duration-150 ${
        selected ? 'bg-brand-surface' : 'hover:bg-zinc-50'
      } ${className}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-zinc-800 truncate" title={d.primary}>
              {d.primary}
            </p>
            {d.secondary && (
              <p className="text-[10px] text-zinc-500 truncate mt-0.5" title={d.secondary}>
                {d.secondary}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {trailing}
          {conv.updated_at && formatTime && (
            <span className="text-[10px] text-zinc-400">{formatTime(conv.updated_at)}</span>
          )}
        </div>
      </div>
      <p className="text-xs text-zinc-500 truncate pl-5">{d.preview}</p>
      <div className="flex flex-wrap items-center gap-1 mt-1.5 pl-5">
        <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0">
          {channelLabel}
        </Badge>
        {d.ticketId && (
          <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0 font-mono">
            #{String(d.ticketId).slice(0, 8)}
          </Badge>
        )}
        {conv.intent && (
          <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0">
            {conv.intent}
          </Badge>
        )}
        <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0">
          {conv.status}
        </Badge>
        {conv.hitl?.is_human_active && (
          <Badge className="text-[10px] rounded-sm bg-brand-danger text-white px-1 py-0">HITL</Badge>
        )}
        {/* `escalated` es el estado que dejan el heartbeat (el usuario no responde) y
            `solicitar_intervencion_humana` (el agente pide una persona). Decia `ESC`, una sigla
            sin leyenda al lado de `HITL`, que es otra cosa: HITL es que **ya hay** una persona
            tomando la conversacion. */}
        {conv.status === 'escalated' && (
          <Badge className="text-[10px] rounded-sm bg-brand-warning text-zinc-900 px-1 py-0"
                 title="Nadie la tomó todavía">
            Falta una persona
          </Badge>
        )}
        {d.pendingOutbound && (
          <Badge className="text-[10px] rounded-sm bg-amber-100 text-amber-800 px-1 py-0">
            Aprobar mail
          </Badge>
        )}
        {hasMergeSuggestion && (
          <Badge className="text-[10px] rounded-sm bg-violet-100 text-violet-800 px-1 py-0">
            Posible duplicado
          </Badge>
        )}
        {conv.triage?.human_reviewed && (
          <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0 text-emerald-700">
            Revisado
          </Badge>
        )}
      </div>
    </button>
  );
}
